import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AssetTypes, CustomEventTypes, ObjectInspectorSectionTypes, RenderModes, TrackerTypes } from '../../globals';
import ARTracker from '../../classes/ARTracker';
import DropdownProperty from '../properties/DropdownProperty';
import PropertyGroup from '../properties/PropertyGroup';
import { EyeIcon, EyeIconOff, ResetIcon } from '../Iconset';
import Spacer from '../layout/Spacer';
import SliderProperty from '../properties/SliderProperty';
import NumberProperty from '../properties/NumberProperty';
import AssetProperty from '../properties/AssetProperty';
import StringProperty from '../properties/StringProperty';
import BoolProperty from '../properties/BoolProperty';
import { TooltipKeys } from '../tooltips/TooltipKeys';
import ContextMenu from '../menus/ContextMenu';
import { useTypedDispatch, useTypedSelector } from '../../state/hooks';
import { StoreActions } from '../../state/store';
import Engine from '../../classes/Engine';
import SetObjectCustomProperty from '../../actions/SetObjectCustomPropertyAction';
import SetObjectCustomProperties from '../../actions/SetObjectCustomPropertiesAction';
import SetModelTrackerModelAction from '../../actions/SetModelTrackerModelAction';
import ModelTrackerModelPicker from '../trackersUI/ModelTrackerModelPicker';
import ModelTrackerCameraPreview from '../trackersUI/ModelTrackerCameraPreview';
import useEventListener from '../reactUtils/useEventListener';

interface ITrackerComponentProps
{
	objects: Array<ARTracker>;
	noGroup?: boolean;
	readOnly?: boolean;
}

const TrackerComponent = (props: ITrackerComponentProps) => 
{	
	useEventListener(CustomEventTypes.onDisplayUnitChange, initializeValues, document);
	useEventListener(CustomEventTypes.updateTrackerComponent, initializeValues, document);

	const dispatch = useTypedDispatch();

	const propertyClipboard = useTypedSelector(state => state.applicationModule.propertyClipboard);

	const [visible, setVisible] = useState(false);
	const [size, setSize] = useState(0);
	const [image, setImage] = useState<string | undefined>();
	const [type, setType] = useState("");
	const [model, setModel] = useState("");
	const [sceneSelectionName, setSceneSelectionName] = useState("");
	const [autoPose, setAutoPose] = useState(false);
	const [laplaceThreshold, setLaplaceThreshold] = useState(0);
	const [normalThreshold, setNormalThreshold] = useState(0);
	const [minInlierRatioInit, setMinInlierRatioInit] = useState(0);
	const [minInlierRatioTracking, setMinInlierRatioTracking] = useState(0);
	const [lineGradientThreshold, setLineGradientThreshold] = useState(0);
	const [lineSearchLengthInitRelative, setLineSearchLengthInitRelative] = useState(0);
	const [lineSearchLengthTrackingRelative, setLineSearchLengthTrackingRelative] = useState(0);
	const [keyFrameDistance, setKeyFrameDistance] = useState(0);
	const [fovPercent, setFovPercent] = useState(0);
	const [multivalues, setMultivalues] = useState<Record<string, any>>({});
	const [displayMode, setDisplayMode] = useState<string>("");
	const [isDefaultTracker, setIsDefaultTracker] = useState(false);
	const [initialSizes, setInitialSizes] = useState<Array<number>>([]);
	const [guideImageAssetId, setGuideImageAssetId] = useState("");
	const [guideTitle, setGuideTitle] = useState("");
	const [guideCaption, setGuideCaption] = useState("");
	const [guideSize, setGuideSize] = useState(1);
	const [guideCaptionVisible, setGuideCaptionVisible] = useState(true);
	const [guideTitleVisible, setGuideTitleVisible] = useState(true);
	const [contentCanBeScaled, setContentCanBeScaled] = useState(true);
	const [contentCanBeRotated, setContentCanBeRotated] = useState(true);

	useEffect(() => 
	{
		initializeValues();
	}, [props.objects]);

	useEffect(() => 
	{
		document.dispatchEvent(new CustomEvent(CustomEventTypes.onTrackerPreviewUpdated, { 
			detail: { 
				guideImageAssetId: guideImageAssetId,
				guideTitle: guideTitle,
				guideCaption: guideCaption,
				guideSize: guideSize,
				guideCaptionVisible: guideCaptionVisible,
				guideTitleVisible: guideTitleVisible,
			}
		}));
	}, [guideImageAssetId, guideTitle, guideCaption, guideSize, guideTitleVisible, guideCaptionVisible]);

	function initializeValues()
	{
		if(!props.objects.length || props.objects.some(obj => !(obj instanceof ARTracker)))
		{
			setVisible(false);
			return;
		}

		const referenceObject = props.objects[0] as ARTracker;
	
		const newMultivalues: Record<string, any> = {};
	
		const newInitialSizes = new Array<number>();
		newInitialSizes.push(referenceObject.getImageSize());
		const typeValue = referenceObject.getTrackerType();
		const displayModeValue = referenceObject.getDisplayMode();
	
		// image
		const imageValue = referenceObject.getImage();
		const sizeValue = newInitialSizes[0];
	
		// model
		const modelValue = referenceObject.getModelAssetId();
		const sceneSelection = referenceObject.getSceneSelectionModels();
		const sceneSelectionValue = (sceneSelection.length > 0) ? (sceneSelection.length > 1) ? "Multiple Objects" : sceneSelection[0].name : "";
		const autoPoseValue = referenceObject.getPoseAutoInit();
		const laplaceThresholdValue = referenceObject.getLaplaceThreshold();
		const normalThresholdValue = referenceObject.getNormalThreshold();
		const minInlierRatioInitValue = referenceObject.getMinInlierRatioInit();
		const minInlierRatioTrackingValue = referenceObject.getMinInlierRatioTracking();
		const lineGradientThresholdValue = referenceObject.getLineGradientThreshold();
		const lineSearchLengthInitRelativeValue = referenceObject.getLineSearchLengthInitRelative();
		const lineSearchLengthTrackingRelativeValue = referenceObject.getLineSearchLengthTrackingRelative();
		const keyFrameDistanceValue = referenceObject.getKeyFrameDistance();
		const fovPercentValue = referenceObject.getFovPercent();
		const guideImageAssetIdValue = referenceObject.getGuideImageAssetId();
		const guideTitleValue = referenceObject.getGuideTitle();
		const guideTitleVisibleValue = referenceObject.getGuideTitleVisible();
		const guideCaptionValue = referenceObject.getGuideCaption();
		const guideCaptionVisibleValue = referenceObject.getGuideCaptionVisible();
		const guideSizeValue = referenceObject.getGuideSize();
		const contentCanBeScaledValue = referenceObject.getContentCanBeScaled();
		const contentCanBeRotatedValue = referenceObject.getContentCanBeRotated();

		//markerless
		const isDefaultTrackerValue = referenceObject.getIsDefaultTracker();
	
		for (let i = 1; i < props.objects.length; i++)
		{
			const object = props.objects[i];
	
			//general
			if (typeValue !== object.getTrackerType()) newMultivalues.type = true;
			if (displayModeValue !== object.getDisplayMode()) newMultivalues.displayMode = true;
	
			//image
			if (imageValue !== object.getImage()) newMultivalues.image = true;
			if (sizeValue !== object.getImageSize()) newMultivalues.size = true;
	
			// model
			if (autoPoseValue !== object.getPoseAutoInit()) newMultivalues.autoPose = false;
	
			if (laplaceThresholdValue !== object.getLaplaceThreshold()) newMultivalues.laplaceThreshold = true;
			if (normalThresholdValue !== object.getNormalThreshold()) newMultivalues.normalThreshold = true;
			if (minInlierRatioInitValue !== object.getMinInlierRatioInit()) newMultivalues.minInlierRatioInit = true;
			if (minInlierRatioTrackingValue !== object.getMinInlierRatioTracking()) newMultivalues.minInlierRatioTracking = true;
			if (lineGradientThresholdValue !== object.getLineGradientThreshold()) newMultivalues.lineGradientThreshold = true;
			if (lineSearchLengthInitRelativeValue !== object.getLineSearchLengthInitRelative()) newMultivalues.lineSearchLengthInitRelative = true;
			if (lineSearchLengthTrackingRelativeValue !== object.getLineSearchLengthTrackingRelative()) newMultivalues.lineSearchLengthTrackingRelative = true;
			if (keyFrameDistanceValue !== object.getKeyFrameDistance()) newMultivalues.keyFrameDistance = true;
			if (fovPercentValue !== object.getFovPercent()) newMultivalues.fovPercent = true;
			if (contentCanBeScaledValue !== object.getContentCanBeScaled()) newMultivalues.contentCanBeScaled = true;
			if (contentCanBeRotatedValue !== object.getContentCanBeRotated()) newMultivalues.contentCanBeRotated = true;
	
			newInitialSizes.push(object.getImageSize());
		}
	
		setVisible(true);
		setSize(sizeValue / Engine.getInstance().getDisplayUnitScale());
		setType(newMultivalues.type ? "" : typeValue);
		setDisplayMode(newMultivalues.displayMode ? "" : displayModeValue);
		setImage(imageValue);
		setModel(modelValue);
		setSceneSelectionName(sceneSelectionValue);
		setAutoPose(autoPoseValue);
		setLaplaceThreshold(laplaceThresholdValue);
		setNormalThreshold(normalThresholdValue);
		setMinInlierRatioInit(minInlierRatioInitValue);
		setMinInlierRatioTracking(minInlierRatioTrackingValue);
		setLineGradientThreshold(lineGradientThresholdValue);
		setLineSearchLengthInitRelative(lineSearchLengthInitRelativeValue);
		setLineSearchLengthTrackingRelative(lineSearchLengthTrackingRelativeValue);
		setKeyFrameDistance(keyFrameDistanceValue);
		setFovPercent(fovPercentValue);
		setIsDefaultTracker(isDefaultTrackerValue);
		setMultivalues(newMultivalues);
		setInitialSizes(newInitialSizes);
		setGuideImageAssetId(guideImageAssetIdValue);
		setGuideTitle(guideTitleValue);
		setGuideTitleVisible(guideTitleVisibleValue);
		setGuideCaption(guideCaptionValue);
		setGuideCaptionVisible(guideCaptionVisibleValue);
		setGuideSize(guideSizeValue);
		setContentCanBeScaled(contentCanBeScaledValue);
		setContentCanBeRotated(contentCanBeRotatedValue);
	}

	function onSizeChanged(value: number)
	{
		if (multivalues.size && isNaN(value)) return;
		multivalues.size = false;

		for (let i = 0; i < props.objects.length; i++)
		{
			props.objects[i].setImageSize(Number(value));
		}
	}

	function onSizeCommitted()
	{
		const newSizes = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			newSizes.push(props.objects[i].getImageSize());
			props.objects[i].setImageSize(initialSizes[i]);
		}

		new SetObjectCustomProperty(props.objects, "setImageSize", [...newSizes], [...initialSizes], "Set AR Tracker Size");
		setInitialSizes(newSizes);
	}

	const onImageCommitted = useCallback((value: string) =>
	{
		setImage(value);

		const newAssetIds = Array(props.objects.length).fill(value);
		const currentIds = new Array<string>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentIds.push(props.objects[i].getImage() || "");
		}

		new SetObjectCustomProperty(props.objects, "setImage", newAssetIds, currentIds, "Set AR Tracker Image");
	}, [props.objects]);

	const onModelCommitted = useCallback((value) =>
	{
		setModel(value);
		new SetModelTrackerModelAction(props.objects, value);
	}, [props.objects]);

	const onSceneSelectionCommitted = useCallback((value) =>
	{
		new SetModelTrackerModelAction(props.objects, value);
	}, [props.objects]);

	const onIsDefaultTrackerComitted = useCallback((value: boolean) =>
	{
		setIsDefaultTracker(value);

		new SetObjectCustomProperty(props.objects, "setIsDefaultTracker", [value], [!value], "Set Markerless Tracker As Default");
	}, [props.objects]);

	const onContentCanBeScaledComitted = useCallback((value: boolean) =>
	{
		setContentCanBeScaled(value);

		new SetObjectCustomProperty(props.objects, "setContentCanBeScaled", [value], [!value], "Set Markerless Tracker Content Can Be Scaled");
	}, [props.objects]);

	const onContentCanBeRotatedComitted = useCallback((value: boolean) =>
	{
		setContentCanBeRotated(value);

		new SetObjectCustomProperty(props.objects, "setContentCanBeRotated", [value], [!value], "Set Markerless Tracker Content Can Be Rotated");
	}, [props.objects]);

	const onAutoPoseCommitted = useCallback((value: boolean) =>
	{
		setAutoPose(value);

		const newValues = Array(props.objects.length).fill(value);
		const currentValues = new Array<boolean>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues.push(props.objects[i].getPoseAutoInit());
		}

		new SetObjectCustomProperty(props.objects, "setPoseAutoInit", newValues, currentValues, "Set Object Tracker Auto-Pose");
	}, [props.objects]);

	const onlaplaceThresholdCommitted = useCallback((value: number) =>
	{
		setLaplaceThreshold(value);

		const newValues = Array(props.objects.length).fill(value);
		const currentValues = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues.push(props.objects[i].getLaplaceThreshold());
		}

		new SetObjectCustomProperty(props.objects, "setLaplaceThreshold", newValues, currentValues, "Set Object Tracker Laplace Threshold");
	}, [props.objects]);

	const onNormalThresholdCommitted = useCallback((value: number) =>
	{
		setNormalThreshold(value);

		const newValues = Array(props.objects.length).fill(value);
		const currentValues = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues.push(props.objects[i].getNormalThreshold());
		}

		new SetObjectCustomProperty(props.objects, "setNormalThreshold", newValues, currentValues, "Set Object Tracker Normal Threshold");
	}, [props.objects]);

	const onMinInlierRatioInitCommitted = useCallback((value: number) =>
	{
		setMinInlierRatioInit(value);

		const newValues = Array(props.objects.length).fill(value);
		const currentValues = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues.push(props.objects[i].getMinInlierRatioInit());
		}

		new SetObjectCustomProperty(props.objects, "setMinInlierRatioInit", newValues, currentValues, "Set Object Tracker Min Inlier Ratio Init");
	}, [props.objects]);

	const onMinInlierRatioTrackingCommitted = useCallback((value: number) =>
	{
		setMinInlierRatioTracking(value);

		const newValues = Array(props.objects.length).fill(value);
		const currentValues = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues.push(props.objects[i].getMinInlierRatioTracking());
		}

		new SetObjectCustomProperty(props.objects, "setMinInlierRatioTracking", newValues, currentValues, "Set Object Tracker Min Inlier Ratio Tracking");
	}, [props.objects]);

	const onLineSearchLengthInitRelativeCommitted = useCallback((value: number) =>
	{
		setLineSearchLengthInitRelative(value);

		const newValues = Array(props.objects.length).fill(value);
		const currentValues = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues.push(props.objects[i].getLineSearchLengthInitRelative());
		}

		new SetObjectCustomProperty(props.objects, "setLineSearchLengthInitRelative", newValues, currentValues, "Set Object Tracker Line Search Length Init Relative");
	}, [props.objects]);

	const onLineSearchLengthTrackingRelativeCommitted = useCallback((value: number) =>
	{
		setLineSearchLengthTrackingRelative(value);

		const newValues = Array(props.objects.length).fill(value);
		const currentValues = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues.push(props.objects[i].getLineSearchLengthTrackingRelative());
		}

		new SetObjectCustomProperty(props.objects, "setLineSearchLengthTrackingRelative", newValues, currentValues, "Set Object Tracker Line Search Length Tracking Relative");
	}, [props.objects]);

	const onKeyFrameDistanceCommitted = useCallback((value: number) =>
	{
		setKeyFrameDistance(value);

		const newValues = Array(props.objects.length).fill(value);
		const currentValues = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues.push(props.objects[i].getKeyFrameDistance());
		}

		new SetObjectCustomProperty(props.objects, "setKeyFrameDistance", newValues, currentValues, "Set Object Tracker Key Frame Distance");
	}, [props.objects]);

	const onLineGradientThresholdCommitted = useCallback((value: number) =>
	{
		setLineGradientThreshold(value);

		const newValues = Array(props.objects.length).fill(value);
		const currentValues = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues.push(props.objects[i].getLineGradientThreshold());
		}

		new SetObjectCustomProperty(props.objects, "setLineGradientThreshold", newValues, currentValues, "Set Object Tracker Line Gradient Threshold");
	}, [props.objects]);

	const onFovPercentCommitted = useCallback((value: number) =>
	{
		setFovPercent(value);

		const newValues = Array(props.objects.length).fill(value);
		const currentValues = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues.push(props.objects[i].getFovPercent());
		}

		new SetObjectCustomProperty(props.objects, "setFovPercent", newValues, currentValues, "Set Object Tracker Active Tracking Visibility Threshold");
	}, [props.objects]);

	const onTypeCommitted = useCallback((value: TrackerTypes) =>
	{
		setType(value);

		const newTypes = Array(props.objects.length).fill(value);
		const currentTypes = new Array<TrackerTypes>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentTypes.push(props.objects[i].getTrackerType());
		}

		new SetObjectCustomProperty(props.objects, "setTrackerType", newTypes, currentTypes, "Set AR Tracker Type");
		dispatch(StoreActions.workspace.setLastSelectedTrackerType(value));
	}, [props.objects]);

	const onDisplayModeCommitted = useCallback((value: RenderModes) =>
	{
		setDisplayMode(value);

		const newModes = Array(props.objects.length).fill(value);
		const currentModes = new Array<RenderModes>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentModes.push(props.objects[i].getDisplayMode());
		}

		new SetObjectCustomProperty(props.objects, "setDisplayMode", newModes, currentModes, "Set AR Tracker Display Mode");
	}, [props.objects]);

	const onGuideImageCommitted = useCallback((value: string) =>
	{
		setGuideImageAssetId(value);

		const newGuideImageIds = Array(props.objects.length).fill(value);
		const currentGuideImageIds = new Array<string>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentGuideImageIds.push(props.objects[i].getGuideImageAssetId());
		}

		new SetObjectCustomProperty(props.objects, "setGuideImageAssetId", newGuideImageIds, currentGuideImageIds, "Set AR Tracker Guide Image");
	}, [props.objects]);

	const onGuideTitleCommitted = useCallback((value: string) =>
	{
		setGuideTitle(value);

		const newGuideTitles = Array(props.objects.length).fill(value);
		const currentGuideTitles = new Array<string>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentGuideTitles.push(props.objects[i].getGuideTitle());
		}

		new SetObjectCustomProperty(props.objects, "setGuideTitle", newGuideTitles, currentGuideTitles, "Set AR Tracker Guide Title");
	}, [props.objects]);

	const onGuideTitleVisibleCommitted = useCallback((value: boolean) => 
	{
		setGuideTitleVisible(value);

		const newGuideTitleVisible = Array(props.objects.length).fill(value);
		const currentGuideTitleVisible = new Array<boolean>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentGuideTitleVisible.push(props.objects[i].getGuideTitleVisible());
		}

		new SetObjectCustomProperty(props.objects, "setGuideTitleVisible", newGuideTitleVisible, currentGuideTitleVisible, "Set AR Tracker Guide Title Visibility");
	}, [props.objects]);
	
	const onGuideCaptionCommitted = useCallback((value: string) => 
	{
		setGuideCaption(value);

		const newGuideCaptions = Array(props.objects.length).fill(value);
		const currentGuideCaptions = new Array<string>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentGuideCaptions.push(props.objects[i].getGuideCaption());
		}

		new SetObjectCustomProperty(props.objects, "setGuideCaption", newGuideCaptions, currentGuideCaptions, "Set AR Tracker Guide Caption");
	}, [props.objects]);

	const onGuideCaptionVisibleCommitted = useCallback((value: boolean) => 
	{
		setGuideCaptionVisible(value);

		const newGuideCaptionVisible = Array(props.objects.length).fill(value);
		const currentGuideCaptionVisible = new Array<boolean>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentGuideCaptionVisible.push(props.objects[i].getGuideCaptionVisible());
		}

		new SetObjectCustomProperty(props.objects, "setGuideCaptionVisible", newGuideCaptionVisible, currentGuideCaptionVisible, "Set AR Tracker Guide Caption Visibility");
	}, [props.objects]);

	const onGuideSizeCommitted = useCallback((value: number) =>
	{
		setGuideSize(value);

		const newGuideSizes = Array(props.objects.length).fill(value);
		const currentGuideSizes = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentGuideSizes.push(props.objects[i].getGuideSize());
		}

		new SetObjectCustomProperty(props.objects, "setGuideSize", newGuideSizes, currentGuideSizes, "Set AR Tracker Guide Size");
	}, [props.objects]);

	function copy()
	{
		dispatch(StoreActions.application.setPropertyClipboard({ 
			type: ObjectInspectorSectionTypes.Tracker, 
			data: { 
				visible: visible,
				size: size,
				type: type,
				displayMode: displayMode,
				image: image,
				model: model,
				sceneSelectionName: sceneSelectionName,
				autoPose: autoPose,
				laplaceThreshold: laplaceThreshold,
				normalThreshold: normalThreshold,
				minInlierRatioInit: minInlierRatioInit,
				minInlierRatioTracking: minInlierRatioTracking,
				lineGradientThreshold: lineGradientThreshold,
				lineSearchLengthInitRelative: lineSearchLengthInitRelative,
				lineSearchLengthTrackingRelative: lineSearchLengthTrackingRelative,
				keyFrameDistance: keyFrameDistance,
				isDefaultTracker: isDefaultTracker,
				guideImageAssetId: guideImageAssetId,
			}
		}));
	}

	function paste()
	{
		if (!propertyClipboard || propertyClipboard.type !== ObjectInspectorSectionTypes.Tracker) return;

		const commands = new Array<string>();
		const newData = new Array<any>();
		const oldData = new Array<any>();

		/*
		Missing Commands for now:
			setIsDefaultTracker
			Edit Pose (button) - not copying the pose
		 */

		commands.push('setImageSize');
		const newValues10 = Array(props.objects.length).fill(propertyClipboard.data.size * Engine.getInstance().getDisplayUnitScale());
		const currentValues10 = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues10.push(props.objects[i].getImageSize());
		}
		newData.push(newValues10);
		oldData.push(currentValues10);

		commands.push('setImage');
		const newValues9 = Array(props.objects.length).fill(propertyClipboard.data.image);
		const currentValues9 = new Array<string | undefined>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues9.push(props.objects[i].getImage());
		}
		newData.push(newValues9);
		oldData.push(currentValues9);

		commands.push('setModel');
		const newValues8 = Array(props.objects.length).fill(propertyClipboard.data.model);
		const currentValues8 = new Array<string>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues8.push(props.objects[i].getModelAssetId());
		}
		newData.push(newValues8);
		oldData.push(currentValues8);

		commands.push('setPoseAutoInit');
		const newValues7 = Array(props.objects.length).fill(propertyClipboard.data.autoPose);
		const currentValues7 = new Array<boolean>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues7.push(props.objects[i].getPoseAutoInit());
		}
		newData.push(newValues7);
		oldData.push(currentValues7);

		commands.push('setLaplaceThreshold');
		const newValues6 = Array(props.objects.length).fill(propertyClipboard.data.laplaceThreshold);
		const currentValues6 = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues6.push(props.objects[i].getLaplaceThreshold());
		}
		newData.push(newValues6);
		oldData.push(currentValues6);

		commands.push('setNormalThreshold');
		const newValues5 = Array(props.objects.length).fill(propertyClipboard.data.normalThreshold);
		const currentValues5 = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues5.push(props.objects[i].getNormalThreshold());
		}
		newData.push(newValues5);
		oldData.push(currentValues5);

		commands.push('setMinInlierRatioInit');
		const newValues4 = Array(props.objects.length).fill(propertyClipboard.data.minInlierRatioInit);
		const currentValues4 = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues4.push(props.objects[i].getMinInlierRatioInit());
		}
		newData.push(newValues4);
		oldData.push(currentValues4);

		commands.push('setMinInlierRatioTracking');
		const newValues3 = Array(props.objects.length).fill(propertyClipboard.data.minInlierRatioTracking);
		const currentValues3 = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues3.push(props.objects[i].getMinInlierRatioTracking());
		}
		newData.push(newValues3);
		oldData.push(currentValues3);

		commands.push('setLineSearchLengthInitRelative');
		const newValues2 = Array(props.objects.length).fill(propertyClipboard.data.lineSearchLengthInitRelative);
		const currentValues2 = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues2.push(props.objects[i].getLineSearchLengthInitRelative());
		}
		newData.push(newValues2);
		oldData.push(currentValues2);

		commands.push('setLineSearchLengthTrackingRelative');
		const newValues1 = Array(props.objects.length).fill(propertyClipboard.data.lineSearchLengthTrackingRelative);
		const currentValues1 = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues1.push(props.objects[i].getLineSearchLengthTrackingRelative());
		}
		newData.push(newValues1);
		oldData.push(currentValues1);

		commands.push('setKeyFrameDistance');
		const newValuesKFD = Array(props.objects.length).fill(propertyClipboard.data.keyFrameDistance);
		const currentValuesKFD = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValuesKFD.push(props.objects[i].getKeyFrameDistance());
		}
		newData.push(newValuesKFD);
		oldData.push(currentValuesKFD);

		commands.push('setLineGradientThreshold');
		const newValuesLGT = Array(props.objects.length).fill(propertyClipboard.data.lineGradientThreshold);
		const currentValuesLGT = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValuesLGT.push(props.objects[i].getLineGradientThreshold());
		}
		newData.push(newValuesLGT);
		oldData.push(currentValuesLGT);

		commands.push('setTrackerType');
		const newTypes = Array(props.objects.length).fill(propertyClipboard.data.type);
		const currentTypes = new Array<TrackerTypes>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentTypes.push(props.objects[i].getTrackerType());
		}
		newData.push(newTypes);
		oldData.push(currentTypes);

		commands.push('setDisplayMode');
		const newModes = Array(props.objects.length).fill(propertyClipboard.data.displayMode);
		const currentModes = new Array<RenderModes>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentModes.push(props.objects[i].getDisplayMode());
		}
		newData.push(newModes);
		oldData.push(currentModes);

		commands.push('setGuideImageAssetId');
		const newGuideImageIds = Array(props.objects.length).fill(propertyClipboard.data.guideImageAssetId);
		const currentGuideImageIds = new Array<string>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentGuideImageIds.push(props.objects[i].getGuideImageAssetId());
		}
		newData.push(newGuideImageIds);
		oldData.push(currentGuideImageIds);
				
		commands.push('setGuideTitle');
		const newGuideTitles = Array(props.objects.length).fill(propertyClipboard.data.guideTitle);
		const currentGuideTitles = new Array<string>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentGuideTitles.push(props.objects[i].getGuideTitle());
		}
		newData.push(newGuideTitles);
		oldData.push(currentGuideTitles);

		commands.push('setGuideCaption');
		const newGuideCaptions = Array(props.objects.length).fill(propertyClipboard.data.guideCaption);
		const currentGuideCaptions = new Array<string>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentGuideCaptions.push(props.objects[i].getGuideCaption());
		}
		newData.push(newGuideCaptions);
		oldData.push(currentGuideCaptions);

		commands.push('setGuideSize');
		const newGuideSizes = Array(props.objects.length).fill(propertyClipboard.data.guideSize);
		const currentGuideSizes = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentGuideSizes.push(props.objects[i].getGuideSize());
		}
		newData.push(newGuideSizes);
		oldData.push(currentGuideSizes);

		new SetObjectCustomProperties(props.objects, commands, newData, oldData, "Paste AR Tracker Properties");
	}

	const renderContent = useMemo(() =>
	{
		let k = 0;

		return (
			<>
				<DropdownProperty
					key={k++}
					data-name={"property-Type"}
					label="Type"
					value={type}
					readOnly={props.readOnly}
					defaultValue={TrackerTypes.IMAGE}
					onChangeCallback={onTypeCommitted}
					options={[
						{ display: "Image Tracker", value: TrackerTypes.IMAGE },
						{ display: "Surface Tracker", value: TrackerTypes.SLAM },
						{ display: "Object Tracker", value: TrackerTypes.MODEL },
					]}
					contextMenuOverride={
						[
							{
								renderObject: "Reset values",
								id: "Reset to default",
								callback: () =>
								{
									onTypeCommitted(TrackerTypes.IMAGE)
								},
							},
						]
					}
				/>
				<DropdownProperty
					key={k++}
					data-name={"property-displayMode"}
					label="Display Mode"
					value={displayMode}
					readOnly={props.readOnly}
					defaultValue={RenderModes.Standalone}
					onChangeCallback={onDisplayModeCommitted}
					options={[
						{ display: "Standalone", value: RenderModes.Standalone },
						{ display: "Overlay", value: RenderModes.Device },
					]}
					contextMenuOverride={
						[
							{
								renderObject: "Reset values",
								id: "Reset to default",
								callback: () =>
								{
									onDisplayModeCommitted(RenderModes.Standalone)
								},
							},
						]
					}
				/>
				{
					!(!multivalues.type && type === TrackerTypes.IMAGE) ? null :
						<>
							<AssetProperty data-name={"property-image"}
								key={k++}
								label={"Image"}
								type={AssetTypes.Image}
								value={image}
								readOnly={props.readOnly}
								required={/*!this.props.readOnly ? true :*/ false}
								defaultValue={""}
								multivalues={multivalues.image}
								onChangeCallback={(assetId) => onImageCommitted(assetId)}
								contextMenuOverride={
									[
										{
											renderObject: "Reset values",
											id: "Reset to default",
											callback: () =>
											{
												onImageCommitted("");
											},
										},
									]
								}
							/>
							<NumberProperty data-name={"property-size"}
								key={k++}
								label="Size"
								min={0}
								step={0.1}
								value={size}
								readOnly={props.readOnly}
								defaultValue={0.1}
								multivalues={multivalues.size}
								unitScaling={true}
								onChangeCallback={onSizeChanged}
								onCommitCallback={onSizeCommitted}
								contextMenuOverride={
									[
										{
											renderObject: "Reset values",
											id: "Reset to default",
											callback: () =>
											{
												onSizeChanged(0.1);
												onSizeCommitted();
											},
										},
									]
								}
							/>
						</>
				}
				{
					(props.objects.length === 1 && !multivalues.type && type === TrackerTypes.SLAM) &&
						<BoolProperty data-name={"property-isDefaultTracker"}
							key={k++}
							label={"Default Surface Tracker"}
							value={isDefaultTracker}
							disabled={props.readOnly}
							defaultValue={true}
							onChangeCallback={(v) => onIsDefaultTrackerComitted(v)}
							contextMenuOverride={
								[
									{
										renderObject: "Reset values",
										id: "Reset to default",
										callback: () =>
										{
											onIsDefaultTrackerComitted(true);
										},
									},
								]
							}
						/>
				}
				{
					(props.objects.length === 1 && !multivalues.type && type === TrackerTypes.SLAM) &&
						<BoolProperty data-name={"property-contentCanBeScaled"}
							key={k++}
							label={"Content Can Be Scaled"}
							value={contentCanBeScaled}
							disabled={props.readOnly}
							defaultValue={true}
							onChangeCallback={(v) => onContentCanBeScaledComitted(v)}
							contextMenuOverride={
								[
									{
										renderObject: "Reset values",
										id: "Reset to default",
										callback: () =>
										{
											onContentCanBeScaledComitted(true);
										},
									},
								]
							}
						/>
				}
				{
					(props.objects.length === 1 && !multivalues.type && type === TrackerTypes.SLAM) &&
						<BoolProperty data-name={"property-contentCanRotate"}
							key={k++}
							label={"Content Can Be Rotated"}
							value={contentCanBeRotated}
							disabled={props.readOnly}
							defaultValue={true}
							onChangeCallback={(v) => onContentCanBeRotatedComitted(v)}
							contextMenuOverride={
								[
									{
										renderObject: "Reset values",
										id: "Reset to default",
										callback: () =>
										{
											onContentCanBeRotatedComitted(true);
										},
									},
								]
							}
						/>
				}
				{
					(props.objects.length === 1 && type === TrackerTypes.MODEL) &&
						<>
							<ModelTrackerModelPicker data-name={"property-model"}
								key={k++}
								label={"Object"}
								modelValue={model}
								sceneSelectionValue={sceneSelectionName}
								readOnly={props.readOnly}
								required={!props.readOnly ? true : false}
								defaultValue={""}
								onAssetChangeCallback={onModelCommitted}
								onSceneSelectionChangeCallback={onSceneSelectionCommitted}
								contextMenuOverride={
									[
										{
											renderObject: "Reset values",
											id: "Reset to default",
											callback: () =>
											{
												onModelCommitted("");
											},
										},
									]
								}
							/>
							<AssetProperty data-name={"property-guide-image"}
								type={AssetTypes.Image}
								label={"Image Guide"}
								value={guideImageAssetId}
								readOnly={props.readOnly}
								onChangeCallback={setGuideImageAssetId}
								onCommitCallback={onGuideImageCommitted}
								contextMenuOverride={
									[
										{
											renderObject: "Reset values",
											id: "Reset to default",
											callback: () =>
											{
												onGuideImageCommitted("");
											},
										},
									]
								}
							/>
							<StringProperty data-name={"property-guide-title"}
								label={"Title"}
								value={guideTitle}
								readOnly={props.readOnly}
								onChangeCallback={setGuideTitle}
								onCommitCallback={onGuideTitleCommitted}
								onContextMenuOverride={
									[
										{
											renderObject: "Reset values",
											id: "Reset to default",
											callback: () =>
											{
												onGuideTitleCommitted("");
											},
										},
									]
								}
								buttons={[
									...guideTitleVisible ? 
										[<EyeIcon onClick={() => onGuideTitleVisibleCommitted(false)} />] 
										: 
										[<EyeIconOff onClick={() => onGuideTitleVisibleCommitted(true)} />],
								]}
							/>
							<StringProperty data-name={"property-guide-caption"}
								label={"Caption"}
								value={guideCaption}
								readOnly={props.readOnly}
								onChangeCallback={setGuideCaption}
								onCommitCallback={onGuideCaptionCommitted}
								onContextMenuOverride={
									[
										{
											renderObject: "Reset values",
											id: "Reset to default",
											callback: () =>
											{
												onGuideCaptionCommitted("");
											},
										},
									]
								}
								buttons={[
									...guideCaptionVisible ? 
										[<EyeIcon onClick={() => onGuideCaptionVisibleCommitted(false)} />] 
										: 
										[<EyeIconOff onClick={() => onGuideCaptionVisibleCommitted(true)} />],
								]}
							/>
							<SliderProperty data-name={"property-guide-size"}
								label={"Guide Size"}
								value={guideSize}
								readOnly={props.readOnly}
								min={0.5}
								max={2}
								step={0.1}
								onChangeCallback={setGuideSize}
								onCommitCallback={onGuideSizeCommitted}
								contextMenuOverride={
									[
										{
											renderObject: "Reset values",
											id: "Reset to default",
											callback: () =>
											{
												onGuideSizeCommitted(1);
											},
										},
									]
								}
							/>
							<BoolProperty data-name={"model-enable-init-pose"}
								key={k++}
								label={"Auto-Pose"}
								value={autoPose}
								disabled={props.readOnly}
								defaultValue={true}
								multivalues={multivalues.autoPose}
								onChangeCallback={onAutoPoseCommitted}
								tooltip={TooltipKeys.ObjectTrackerAutoPose}
								contextMenuOverride={
									[
										{
											renderObject: "Reset values",
											id: "Reset to default",
											callback: () =>
											{
												onAutoPoseCommitted(true);
											},
										},
									]
								}
							/>
							{
								autoPose || !model ? null :
									<ModelTrackerCameraPreview
										key={k++}
										tracker={props.objects[0]}
										readOnly={props.readOnly}
									/>
							}
							<NumberSliderCombo
								key={k++}
								defaultValue={0.6}
								value={minInlierRatioInit}
								readOnly={props.readOnly}
								multivalues={multivalues.minInlierRatioInit}
								onChangeCallback={setMinInlierRatioInit}
								onCommitCallback={onMinInlierRatioInitCommitted}
								decimals={2}
								step={0.01}
								min={0.1}
								max={1.0}
								tooltip={TooltipKeys.ObjectTrackerInlierRatio}
								numberProps={{
									["data-name"]: "property-inlier-ratio-tracking-initiation-field",
									label: "Inlier ratio tracking initiation",
								}}
								sliderProps={{
									["data-name"]: "property-minInlierRatioInit",
									hideField: true,
									showMinMax: true,
								}}
								synced={true}
								contextMenuOverride={
									[
										{
											renderObject: "Reset values",
											id: "Reset to default",
											callback: () =>
											{
												setMinInlierRatioInit(0.6);
												onMinInlierRatioInitCommitted(0.6);
											},
										},
									]
								}
							/>
							<NumberSliderCombo
								key={k++}
								min={0.1}
								max={1}//this.state.minInlierRatioInit}
								step={0.01}
								value={minInlierRatioTracking}
								readOnly={props.readOnly}
								defaultValue={0.4}
								multivalues={multivalues.minInlierRatioTracking}
								onChangeCallback={setMinInlierRatioTracking}
								onCommitCallback={onMinInlierRatioTrackingCommitted}
								decimals={2}
								tooltip={TooltipKeys.ObjectTrackerInlierRatioMinimum}
								numberProps={{
									["data-name"]: "property-inlier-ratio-tracking-minimum-field",
									label: "Inlier ratio tracking minimum",
								}}
								sliderProps={{
									["data-name"]: "property-minInlierRatioTracking",
									hideField: true,
									showMinMax: true,
								}}
								synced={true}
								contextMenuOverride={
									[
										{
											renderObject: "Reset values",
											id: "Reset to default",
											callback: () =>
											{
												setMinInlierRatioTracking(0.4);
												onMinInlierRatioTrackingCommitted(0.4);
											},
										},
									]
								}
							/>
							<NumberSliderCombo
								key={k++}
								value={laplaceThreshold}
								readOnly={props.readOnly}
								defaultValue={1.0}
								min={1}
								max={100}
								step={1}
								multivalues={multivalues.laplaceThreshold}
								onChangeCallback={setLaplaceThreshold}
								onCommitCallback={onlaplaceThresholdCommitted}
								decimals={3}
								tooltip={TooltipKeys.ObjectTrackerLaplaceThreshold}
								numberProps={{
									["data-name"]: "property-laplace-threshold-field",
									label: "Laplace threshold",
								}}
								sliderProps={{
									["data-name"]: "property-laplaceThreshold",
									hideField: true,
									showMinMax: true,
								}}
								synced={true}
								contextMenuOverride={
									[
										{
											renderObject: "Reset values",
											id: "Reset to default",
											callback: () =>
											{
												setLaplaceThreshold(1.0);
												onlaplaceThresholdCommitted(1.0);
											},
										},
									]
								}
							/>
							<PropertyGroup title={"Advanced Object Tracking Settings"}
								key={k++}
								expanded={false}
								decorators={[
									<ResetIcon scale={0.75}
										key={k++}
										onClick={() =>
										{
											resetAdvancedSettingsToDefault();
										}}
									/>
								]}
							>
								<NumberSliderCombo
									key={k++}
									min={0}
									max={3}
									step={0.01}
									value={normalThreshold}
									readOnly={props.readOnly}
									defaultValue={0.3}
									multivalues={multivalues.normalThreshold}
									onChangeCallback={setNormalThreshold}
									onCommitCallback={onNormalThresholdCommitted}
									decimals={3}
									tooltip={TooltipKeys.ObjectTrackerNormalThreshold}
									numberProps={{
										["data-name"]: "property-normal-threshold-field",
										label: "Normal threshold",
									}}
									sliderProps={{
										["data-name"]: "property-normalThreshold",
										hideField: true,
										showMinMax: true,
									}}
									synced={true}
									contextMenuOverride={
										[
											{
												renderObject: "Reset values",
												id: "Reset to default",
												callback: () =>
												{
													setNormalThreshold(0.3);
													onNormalThresholdCommitted(0.3);
												},
											},
										]
									}
								/>
								<NumberSliderCombo
									key={k++}
									min={10}
									max={500}
									step={1}
									value={keyFrameDistance}
									readOnly={props.readOnly}
									defaultValue={50}
									multivalues={multivalues.keyFrameDistance}
									onChangeCallback={setKeyFrameDistance}
									onCommitCallback={onKeyFrameDistanceCommitted}
									decimals={1}
									tooltip={TooltipKeys.ObjectTrackerKeyframeDistance}
									numberProps={{
										["data-name"]: "property-keyframe-distance-field",
										label: "Keyframe distance",
									}}
									sliderProps={{
										["data-name"]: "property-keyFrameDistance",
										hideField: true,
										showMinMax: true,
									}}
									synced={true}
									contextMenuOverride={
										[
											{
												renderObject: "Reset values",
												id: "Reset to default",
												callback: () =>
												{
													setKeyFrameDistance(50);
													onKeyFrameDistanceCommitted(50);
												},
											},
										]
									}
								/>
								<NumberSliderCombo
									key={k++}
									min={10}
									max={100}
									step={1}
									value={lineGradientThreshold}
									readOnly={props.readOnly}
									defaultValue={40}
									multivalues={multivalues.lineGradientThreshold}
									onChangeCallback={setLineGradientThreshold}
									onCommitCallback={onLineGradientThresholdCommitted}
									decimals={1}
									tooltip={TooltipKeys.ObjectTrackerLineGradientThreshold}
									numberProps={{
										["data-name"]: "property-line-gradient-threshold-field",
										label: "Line gradient threshold",
									}}
									sliderProps={{
										["data-name"]: "property-lineGradientThreshold",
										hideField: true,
										showMinMax: true,
									}}
									synced={true}
									contextMenuOverride={
										[
											{
												renderObject: "Reset values",
												id: "Reset to default",
												callback: () =>
												{
													setLineGradientThreshold(40);
													onLineGradientThresholdCommitted(40);
												},
											},
										]
									}
								/>
								<NumberSliderCombo
									key={k++}
									min={0}
									max={10}
									step={0.01}
									value={lineSearchLengthTrackingRelative * 100}
									readOnly={props.readOnly}
									defaultValue={3.13}
									multivalues={multivalues.lineSearchLengthTrackingRelative}
									onChangeCallback={(v) => setLineSearchLengthTrackingRelative(v / 100 )}
									onCommitCallback={(v) => onLineSearchLengthTrackingRelativeCommitted(v / 100)}
									decimals={5}
									tooltip={TooltipKeys.ObjectTrackerLineSearchLengthInit}
									numberProps={{
										["data-name"]: "property-line-search-length-tracking-relative-field",
										label: "Line search length during tracking"
									}}
									sliderProps={{
										["data-name"]: "property-lineSearchLengthTrackingRelative",
										hideField: true,
										showMinMax: true,
									}}
									synced={true}
									contextMenuOverride={
										[
											{
												renderObject: "Reset values",
												id: "Reset to default",
												callback: () =>
												{
													setLineSearchLengthTrackingRelative(3.13);
													onLineSearchLengthTrackingRelativeCommitted(3.13);
												},
											},
										]
									}
								/>
								<NumberSliderCombo
									key={k++}
									min={0}
									max={10}
									step={0.01}
									value={lineSearchLengthInitRelative * 100}
									readOnly={props.readOnly}
									defaultValue={4.13}
									multivalues={multivalues.lineSearchLengthInitRelative}
									onChangeCallback={(v) => setLineSearchLengthInitRelative(v  / 100)}
									onCommitCallback={(v) => onLineSearchLengthInitRelativeCommitted(v / 100)}
									decimals={5}
									tooltip={TooltipKeys.ObjectTrackerLineSearchLengthTracking}
									numberProps={{
										["data-name"]: "property-line-search-length-init-relative-field",
										label: "Line search length before tracking",
									}}
									sliderProps={{
										["data-name"]: "property-lineSearchLengthInitRelative",
										hideField: true,
										showMinMax: true,
									}}
									synced={true}
									contextMenuOverride={
										[
											{
												renderObject: "Reset values",
												id: "Reset to default",
												callback: () =>
												{
													setLineSearchLengthInitRelative(4.13);
													onLineSearchLengthInitRelativeCommitted(4.13);
												},
											},
										]
									}
								/>
								<NumberSliderCombo
									key={k++}
									min={5}
									max={95}
									step={0.01}
									value={fovPercent * 100}
									readOnly={props.readOnly}
									defaultValue={0.6}
									multivalues={multivalues.fovPercent}
									onChangeCallback={(v) => setFovPercent(v  / 100)}
									onCommitCallback={(v) => onFovPercentCommitted(v / 100)}
									decimals={5}
									tooltip={TooltipKeys.ObjectTrackerFovPercent}
									numberProps={{
										["data-name"]: "property-fov-percent-field",
										label: "Active Tracking Visibility Threshold",
									}}
									sliderProps={{
										["data-name"]: "property-fov-percent-slider",
										hideField: true,
										showMinMax: true,
									}}
									synced={true}
									contextMenuOverride={
										[
											{
												renderObject: "Reset values",
												id: "Reset to default",
												callback: () =>
												{
													setFovPercent(0.6);
													onFovPercentCommitted(0.6);
												},
											},
										]
									}
								/>
							</PropertyGroup>
						</>
				}
			</>
		);
	}, [props.objects, props.readOnly, type, displayMode, image, size, isDefaultTracker, model, sceneSelectionName, autoPose, laplaceThreshold, normalThreshold, minInlierRatioInit, minInlierRatioTracking, lineSearchLengthInitRelative, lineSearchLengthTrackingRelative, keyFrameDistance, lineGradientThreshold, guideImageAssetId, guideTitle, guideCaption, guideSize, multivalues]);

	const resetAdvancedSettingsToDefault = useCallback(() =>
	{
		const advancedDefaultValues = {
			lineSearchLengthInitRelative: 0.0413,
			lineSearchLengthTrackingRelative: 0.0313,
			lineGradientThreshold: 40,
			keyFrameDistance: 50,
			normalThreshold: 0.3,
			fovPercent: 0.6,
		};

		const commands = new Array<string>();
		const newData = new Array<any>();
		const oldData = new Array<any>();

		commands.push('setNormalThreshold');
		const newValues5 = Array(props.objects.length).fill(advancedDefaultValues.normalThreshold);
		const currentValues5 = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues5.push(props.objects[i].getNormalThreshold());
		}
		newData.push(newValues5);
		oldData.push(currentValues5);

		commands.push('setKeyFrameDistance');
		const newValuesKFD = Array(props.objects.length).fill(advancedDefaultValues.keyFrameDistance);
		const currentValuesKFD = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValuesKFD.push(props.objects[i].getKeyFrameDistance());
		}
		newData.push(newValuesKFD);
		oldData.push(currentValuesKFD);

		commands.push('setFovPercent');
		const newValuesFov = Array(props.objects.length).fill(advancedDefaultValues.fovPercent);
		const currentValuesFov = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValuesFov.push(props.objects[i].getFovPercent());
		}
		newData.push(newValuesFov);
		oldData.push(currentValuesFov);

		commands.push('setLineGradientThreshold');
		const newValuesLGT = Array(props.objects.length).fill(advancedDefaultValues.lineGradientThreshold);
		const currentValuesLGT = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValuesLGT.push(props.objects[i].getLineGradientThreshold());
		}
		newData.push(newValuesLGT);
		oldData.push(currentValuesLGT);

		commands.push('setLineSearchLengthTrackingRelative');
		const newValues1 = Array(props.objects.length).fill(advancedDefaultValues.lineSearchLengthTrackingRelative);
		const currentValues1 = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues1.push(props.objects[i].getLineSearchLengthTrackingRelative());
		}
		newData.push(newValues1);
		oldData.push(currentValues1);

		commands.push('setLineSearchLengthInitRelative');
		const newValues2 = Array(props.objects.length).fill(advancedDefaultValues.lineSearchLengthInitRelative);
		const currentValues2 = new Array<number>();
		for (let i = 0; i < props.objects.length; i++)
		{
			currentValues2.push(props.objects[i].getLineSearchLengthInitRelative());
		}
		newData.push(newValues2);
		oldData.push(currentValues2);

		new SetObjectCustomProperties(props.objects, commands, newData, oldData, "Reset Advanced AR Tracker Properties");
	}, [props.objects]);

	const isMultiValues = useMemo(() =>
	{
		const str = JSON.stringify(multivalues);
		if (str.toLocaleLowerCase().includes('true')) return true;
		return false;
	}, [multivalues]);

	// don't render if we're not dealing with tracker objects
	if(!visible || props.objects.some(obj => !(obj instanceof ARTracker)))
	{
		return null;
	}

	return (
		props.noGroup ? renderContent :
			<PropertyGroup
				title="AR Tracker"
				onContextMenu={(e) =>
				{
					e.preventDefault();
					ContextMenu.show(e, [], "Tracker", [
						{
							items:
							[
								...(!isMultiValues ? [{
									renderObject: "Copy",
									id: "copy",
									callback: copy,
								}] : []),
								...(propertyClipboard?.type === ObjectInspectorSectionTypes.Tracker && !props.readOnly ? [{
									renderObject: "Paste",
									id: "paste",
									callback: paste,
								}] : [])
							],
						},
					]);
				}}
			>
				{renderContent}
			</PropertyGroup >
	);
};

export default TrackerComponent;

function NumberSliderCombo(props)
{
	const numberProps = { ...props, ...props.numberProps };
	const sliderProps = { ...props, ...props.sliderProps };

	return (
		<div>
			<NumberProperty {...numberProps} className={"comboProperty"} showResetButton={true} />
			<Spacer size={{ y: "4px" }} />
			<SliderProperty {...sliderProps} className={"comboProperty"} />
			<Spacer size={{ y: "10px" }} />
		</div>
	);
}